import { useTranslation } from 'react-i18next';
import { Select, Tooltip } from '@iba-team/portal-react-components/dist';
import { observer } from 'mobx-react';
import './LeaveTypes.scss';
import { useState } from 'react';
import { fixFirstLetterToUpper } from '../utils/functions';
import { LeaveTypesI } from '../types';
import { useWindowSize } from 'react-use';

import businessTripSvg from '../assets/icons/leaveTypes/business-trip.svg';
import childcareExtraLeaveSvg from '../assets/icons/leaveTypes/childcare-extra-leave.svg';
import childcareSickLeaveSvg from '../assets/icons/leaveTypes/childcare-sick-leave.svg';
import compensatoryOffSvg from '../assets/icons/leaveTypes/compensatory-off.svg';
import dayOffSvg from '../assets/icons/leaveTypes/day-off.svg';
import extraLeaveSvg from '../assets/icons/leaveTypes/extra-leave.svg';
import forceMajeureSvg from '../assets/icons/leaveTypes/force-majeure.svg';
import jobSearchSvg from '../assets/icons/leaveTypes/job-search.svg';
import maternityLeaveSvg from '../assets/icons/leaveTypes/maternity-leave.svg';
import militaryLeaveSvg from '../assets/icons/leaveTypes/military-leave.svg';
import occasionalLeaveSvg from '../assets/icons/leaveTypes/occasional-leave.svg';
import parentalLeaveSvg from '../assets/icons/leaveTypes/parental-leave.svg';
import paternityLeaveSvg from '../assets/icons/leaveTypes/paternity-leave.svg';
import sickLeaveSvg from '../assets/icons/leaveTypes/sick-leave.svg';
import unexcusedAbsenceSvg from '../assets/icons/leaveTypes/unexcused-absence.svg';
import unpaidCareLeaveSvg from '../assets/icons/leaveTypes/unpaid-care-leave.svg';
import unpaidLeaveSvg from '../assets/icons/leaveTypes/unpaid-leave.svg';
import unpaidMaternityLeaveSvg from '../assets/icons/leaveTypes/unpaid-maternity-leave.svg';
import unpaidParentalLeaveSvg from '../assets/icons/leaveTypes/unpaid-parental-leave.svg';
import vacationSvg from '../assets/icons/leaveTypes/vacation.svg';
import bloodDonationSvg from '../assets/icons/leaveTypes/blood-donation.svg';
import absenceGDPRSvg from '../assets/icons/leaveTypes/absenceGDPR.svg';

const leaveTypeIcons: Record<string, string> = {
  'business_trip': businessTripSvg,
  'childcare_extra_leave': childcareExtraLeaveSvg,
  'childcare_sick_leave': childcareSickLeaveSvg,
  'compensatory_off': compensatoryOffSvg,
  'day_off': dayOffSvg,
  'extra_leave': extraLeaveSvg,
  'force_majeure': forceMajeureSvg,
  'job_search': jobSearchSvg,
  'maternity_leave': maternityLeaveSvg,
  'military_leave': militaryLeaveSvg,
  'occasional_leave': occasionalLeaveSvg,
  'parental_leave': parentalLeaveSvg,
  'paternity_leave': paternityLeaveSvg,
  'sick_leave': sickLeaveSvg,
  'unexcused_absence': unexcusedAbsenceSvg,
  'unpaid_care_leave': unpaidCareLeaveSvg,
  'unpaid_leave': unpaidLeaveSvg,
  'unpaid_maternity_leave': unpaidMaternityLeaveSvg,
  'unpaid_parental_leave': unpaidParentalLeaveSvg,
  'vacation': vacationSvg,
  'vacation_(on_demand)': vacationSvg,
  'blood_donation': bloodDonationSvg,
  'absence': absenceGDPRSvg,
};

export const requireIcon = (type: string) => {
  const key = type.toLowerCase().replace(/_|\s+/g, '_');
  return leaveTypeIcons[key] ?? '';
};


interface LeaveType{
  type: string,
  infoMessageVisible: boolean,
  chosenTypePass?: (e: number) => void,
  withoutText?: true
  defaultValue?: any
  disabled?: boolean,
  leaveTypes: LeaveTypesI[],
  value?: number
}

const LeaveTypes = observer(({ type, chosenTypePass, defaultValue, withoutText, infoMessageVisible, disabled, leaveTypes, value } : LeaveType) => {
  const { t } = useTranslation();
  const [selectedLeaveType, setSelectedLeaveType] = useState<LeaveTypesI>();
  const {width} = useWindowSize();
  const isWidth = width < 1280;
  const renderType = (type: string) => {
    if(leaveTypes.length === 0) {
      return ;
    }

    const typeFound = leaveTypes.map(i => i.type === type);
    if(!typeFound) {
      throw new Error(`${type} unrecognized type`);
    }

    if(type === 'all') {
      return (
        leaveTypes.map(i => (
          <span>
            <img src={ requireIcon(i.requestName) } alt={'leaveType'}/>
            {!withoutText && <span className='types-living'>{isWidth ?  fixFirstLetterToUpper(i.requestName) : i.requestName }</span> }
          </span>))
      );
    }

    return (
      <Tooltip title={ infoMessageVisible ? leaveTypes.find(i => i.requestName === type)?.tooltip : null}>
        <span className={'leave-type'}>
          <img src={ requireIcon(type) } alt={'leaveType'}/>
          {!withoutText && <span style={{ marginLeft: '4px'}}>{ type }</span> }
        </span>
      </Tooltip>
    );
  };

  const handledValue = () => {
    if(value && leaveTypes.length){
      const allLeaveTypes = leaveTypes.flatMap(t => [
        t,
        ...(t.requestSubTypes ?? [])
      ]);
      const leaveType = allLeaveTypes.find(leave => leave.requestTypeId === value)!;
      return <>
        <img src={requireIcon(leaveType.requestName)} style={{ padding: '0 5px 3px 0'}} alt={'leaveType'}/>
        <span>{ leaveType?.requestName }</span>
      </>;
    }
    return undefined;
  };
  return <>
    { type !== '' ? renderType(type) :
      <Select
        disabled={disabled}
        value={ defaultValue && !value ? defaultValue.label : handledValue()}
        placeholder={ t('chooseLeaveType') }
        onChangeValue={(e) => {
          setSelectedLeaveType(e);
          chosenTypePass?.(e.requestTypeId);
        } }
        list={leaveTypes}
        render={leaveType =>  <Tooltip title={ leaveType.tooltip }>
          <img src={requireIcon(leaveType.requestName)} style={{ padding: '0 5px 3px 0'}} alt={'leaveType'}/>
          <span>{ leaveType.requestName }</span>
        </Tooltip>} uniqueElement={type => type.requestTypeId === selectedLeaveType?.requestTypeId}/>
    }
  </>;
});

export default LeaveTypes;
